import type { ComponentType } from "react";
import { Link } from "@tanstack/react-router";
import familyPhoto from "@/assets/family-photo-insurance.webp.asset.json";
import salesNavigatorPhoto from "@/assets/strategy-session-sales-navigator.webp.asset.json";
import advisorEnablementLake from "@/assets/advisor-enablement-lake.png.asset.json";
import type { BlogCoverKey } from "@/lib/blog-covers";

const CALENDLY = "https://calendly.com/katie-fernands/discovery-call";

const BookCallButton: ComponentType<{ label?: string; variant?: "primary" | "subtle" }> = ({
  label = "Book a 20-minute call",
  variant = "primary",
}) => {
  const base =
    "inline-flex items-center gap-2 rounded-full font-medium transition-opacity no-underline";
  const styles =
    variant === "primary"
      ? "bg-primary text-primary-foreground px-6 py-3 hover:opacity-90"
      : "border border-primary/30 text-primary px-5 py-2.5 hover:bg-primary/10";
  return (
    <a href={CALENDLY} target="_blank" rel="noopener noreferrer" className={`${base} ${styles}`}>
      {label} →
    </a>
  );
};

export type Article = {
  slug: string;
  /**
   * URL prefix the article is served from. "/f" is reserved for posts
   * rehomed from the old GoDaddy site so their indexed URLs keep working.
   */
  basePath?: "/blog" | "/f";
  title: string;
  titleTag: string;
  metaDescription: string;
  date: string;
  readingTime: string;
  keywords: string[];
  /** Optional hero image, used at the top of the article, on the Insights card, and for social previews. */
  image?: { url: string; alt: string; cover?: BlogCoverKey };
  faq?: { question: string; answer: string }[];
  Body: ComponentType;
};

const FinancialAdvisorMarketingPlan: ComponentType = () => (
  <>
    <p className="lead">
      The average advisory firm spends about $3,000 to land one new client. More than 80% of that
      isn't ad spend — it's the advisor's own hours, sitting in networking events and coffee
      meetings that may or may not turn into anything. Most advisors can't tell you which of their
      marketing actually worked last year. That's not a spending problem. It's a system problem.
    </p>
    <p>
      This isn't another list of generic small-business marketing tips with "compliance" bolted on.
      It's a plan built specifically for RIAs, banks, and fintech advisors, from 15 years spent
      inside the industry, not consulting it from the outside. By the end, you'll have a five-part
      framework you can actually run.
    </p>
    <p className="mt-8">
      <BookCallButton variant="subtle" />
    </p>

    <h2>Why most financial advisor marketing fails</h2>
    <p>
      Three things account for almost every advisor's marketing budget, and none of them scale.
    </p>
    <p>
      <strong>Cold calling is dying</strong> — and it never worked that well to begin with. It's
      low-ROI, and every call chips away at the trust you need for the actual sale.
    </p>
    <p>
      <strong>Referral-only growth</strong> feels safe because it's free, but it's unpredictable.
      Kitces Research found that referrals still bring in nearly two-thirds of new clients
      industry-wide, and they return almost $5 in revenue for every $1 spent — which sounds great
      until you realize you don't control the tap. You're waiting for someone else to think of you.
    </p>
    <p>
      <strong>Generic content</strong> is the third trap. "5 retirement tips" doesn't differentiate
      you from the other 500 advisors who wrote the same post this month.
    </p>
    <p>
      None of these are tactics problems. They're the absence of a system. That's the actual gap.
    </p>

    <h2>The inbound pipeline method</h2>
    <p>
      Five parts. Run them in order, and each one makes the next one work better.
    </p>

    <h3>1. Positioning: own a specific niche, not a broad market</h3>
    <p>
      Generalists compete on price. Specialists command premium fees, because a prospect who fits
      your niche exactly doesn't need to be sold — they need to be found.
    </p>
    <p>
      Mine: I work with RIA firms, banks, and fintech advisory teams in the 4-to-20-employee range,
      where firm leadership is the buyer. The pattern is almost always the same — the founders
      built the book on relationships and referrals, and now growth has plateaued because there's
      no repeatable pipeline sitting underneath the practice. That's specific enough that the
      right firm leader reads it and thinks, <em>that's us</em>, instead of skimming past another
      generic pitch aimed at solo advisors.
    </p>
    <p>
      Niching down isn't discriminatory targeting — it's message clarity. And it works: Kitces
      Research found niche advisory firms average 58% client growth, compared to 26% for firms that
      stay generalist. That's more than double.
    </p>
    <p>
      Your one-sentence positioning statement should say who you serve, what problem you solve for
      them, and why you're different from the advisor down the street. If you can't write that
      sentence yet, that's the actual starting point — before any content or outreach.
    </p>

    <h3>2. Content: become the source people cite</h3>
    <p>
      Long-form content isn't about traffic. It's about being the answer when someone Googles a
      question you could answer in your sleep, or when an AI tool gets asked "how should an RIA
      think about marketing" and needs something well-structured to cite.
    </p>
    <p>
      One article, written well, becomes five LinkedIn posts, a handful of outreach hooks, and a
      reason to reach back out to a warm connection: "saw you were thinking about X, wrote
      something on it." Content compounds. Ads reset to zero the day you stop paying for them.
    </p>
    <p>
      A realistic cadence is one or two long-form pieces a month, each answering one specific
      question your ideal client is already asking, cleared with your CCO before it goes live.
    </p>

    <h3>3. LinkedIn: where your buyers actually are</h3>
    <p>
      Your personal profile will outperform a company page every time, because people trust people,
      not logos.
    </p>
    <p>
      Get the basics right first: a headline that says what you do and who you do it for — not your
      job title — a banner that reinforces it, and an About section written for the prospect
      reading it, not for a recruiter. Comment on your prospects' posts before you ever try to
      connect. When you do reach out, invite them to something useful — don't pitch in the DM.
    </p>

    <h3>4. Warm outreach: the anti-cold-call</h3>
    <p>
      This is not cold calling with better manners. It's systematic, not sporadic, referral
      requests, done on a quarterly cadence instead of whenever you happen to remember. It's
      LinkedIn introductions through people you already know. It's sending something useful — an
      article, an insight, an intro — before you ask for anything.
    </p>
    <p>
      Start with the 20 warm connections you haven't touched in six months. That list alone is
      usually worth more than a quarter's worth of cold outreach.
    </p>

    <h3>5. Measurement: what actually matters</h3>
    <p>
      Followers and impressions feel good and mean almost nothing. What matters is how many real
      conversations you booked, what percentage turned into clients, and what each client actually
      cost you to acquire — including your time, not just your ad spend.
    </p>
    <p>Review three numbers monthly. You don't need a dashboard — you need a habit.</p>

    <h2>A 90-day rollout</h2>
    <ul>
      <li>
        <strong>Days 1–30:</strong> write your positioning statement, clean up your LinkedIn
        profile, and publish your first article.
      </li>
      <li>
        <strong>Days 31–60:</strong> publish on LinkedIn weekly, and start warm outreach to your
        list of 20.
      </li>
      <li>
        <strong>Days 61–90:</strong> publish a second article, look at your three numbers, and
        adjust whatever isn't working.
      </li>
    </ul>
    <div className="my-10 rounded-2xl border border-border/60 bg-muted p-6 md:p-8">
      <p className="lead !mt-0 !mb-4">
        Want this plan built for your firm instead of pieced together from a blog post?
      </p>
      <BookCallButton label="Book a 20-minute strategy call" />
    </div>

    <h2>What compliance actually allows</h2>
    <p>
      Most advisors are more restricted by what they assume than by what the rule actually says.
      The SEC's 2021 marketing rule, in effect since November 2022, allows testimonials and
      endorsements, with disclosure of whether the person is a client and whether they were
      compensated for saying it. Performance claims are still tightly restricted. Social content is
      allowed, but it has to be archived.
    </p>
    <p>
      Loop in your CCO at the start of your content calendar, not after you've already written six
      posts they'll want changed. This isn't legal advice — talk to your own compliance team before
      you publish anything.
    </p>

    <h2>The biggest mistake I see advisors make</h2>
    <p>
      They treat marketing like a project instead of a system. They hire an agency for a
      three-month sprint, don't see a client by month four, and pull the plug.
    </p>
    <p>
      I watched one advisor do the opposite. He stayed visible, consistently, with real clarity
      about who he served and what he stood for. No campaign, no big spend — just showing up the
      same way, in the same places, talking about the same things, month after month. He went from
      one or two leads a month to getting stopped constantly out in the community, people
      referencing something he'd posted or said. Marketing writer Seth Godin has a name for this —
      the Purple Cow. A field of brown cows is invisible after the first one. The one that's
      different is the one you notice. Consistency plus a real point of view is what makes you the
      purple cow instead of another brown one.
    </p>
    <p>
      Inbound takes six to twelve months to compound. That's the part nobody wants to hear, and
      it's also the part that's true. Once it compounds, it doesn't stop the way a cold-call list
      does.
    </p>

    <h2>If you want help building yours</h2>
    <p>
      If you'd rather have this built for your specific firm than build it yourself from a blog
      post, book a 20-minute call. We'll look at what you're already doing, what's missing, and
      whether the five-part plan above needs to flex for your situation.
    </p>
    <p>
      <a
        href={CALENDLY}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-medium hover:opacity-90 transition-opacity no-underline"
      >
        Book a 20-minute call →
      </a>
    </p>

    <h2>Frequently asked questions</h2>
    <h3>How much should a financial advisor spend on marketing?</h3>
    <p>
      Kitces Research puts the average around 7% of revenue, though the number matters less than
      where it goes. Most of your real cost is time, not ad spend, so track both.
    </p>
    <h3>What's the best marketing channel for financial advisors?</h3>
    <p>
      There's no single best channel. Referrals convert best per dollar spent, but they don't scale
      on their own. LinkedIn and long-form content build the system that makes referrals and warm
      outreach work better over time.
    </p>
    <h3>Can financial advisors use client testimonials?</h3>
    <p>
      Yes, since the SEC's 2021 marketing rule. You have to disclose whether the person is a client
      and whether they were compensated, and in most cases you'll need a written agreement with
      them. Confirm the specifics with your CCO.
    </p>
    <h3>How long does inbound marketing take to work for advisors?</h3>
    <p>
      Plan on six to twelve months before it compounds into a predictable flow of conversations.
      It's slower to start than cold outreach and faster to scale once it's running.
    </p>
    <h3>Do I need a marketing agency, or can I do this myself?</h3>
    <p>
      You can build the framework yourself. What's harder to do alone is staying consistent for
      the first six months before you see traction, and knowing which of the five parts is
      actually your weak link. That's usually where outside help pays for itself.
    </p>
  </>
);

const SellingInsuranceInFiveMinutes: ComponentType = () => (
  <>
    <p className="lead">
      The life insurance industry is in the middle of a major shift. Customers expect simple,
      digital-first buying experiences. Partners want seamless ways to integrate protection
      products into their existing journeys. And carriers need distribution models that deliver
      speed, clarity, and measurable results.
    </p>
    <p>
      During my time leading partner marketing for Prudential's simplified issue life insurance
      business, I helped guide this transformation. We built and scaled a partnership distribution
      engine that supported Prudential's move to a true few-clicks insurance experience, and the
      impact was undeniable.
    </p>
    <p>Our results included:</p>
    <ul>
      <li>183% improvement in conversion rate</li>
      <li>20% of total sales nurtured back into the funnel</li>
      <li>83% faster time to market with partner campaigns</li>
    </ul>
    <p>
      This piece breaks down what we changed, why it mattered, and how other insurance and
      financial services teams can replicate these outcomes.
    </p>

    <h2>Understanding the challenge: traditional distribution slows growth</h2>
    <p>
      Life insurance has long relied on long forms, multi-step interactions, delayed underwriting,
      and high abandonment rates. Even "simplified issue" wasn't always simple. Customers still had
      to complete multiple steps, re-enter their data, and navigate unclear application flows.
    </p>
    <p>
      Partners (banks, fintechs, loyalty programs, membership groups) wanted to offer insurance in
      a way that matched their brand experience. That meant:
    </p>
    <ul>
      <li>Clear language</li>
      <li>Fewer steps</li>
      <li>No medical exam</li>
      <li>Immediate decisioning</li>
      <li>A natural fit within their existing customer journey</li>
    </ul>
    <p>
      Simplified issue products created the opportunity. Partnership distribution unlocked the
      scale.
    </p>

    <h2>What few-clicks insurance changed</h2>
    <p>
      The biggest breakthrough came from reducing the application down to a single action. Instead
      of redirecting customers to long forms and starting the process from scratch, we passed
      pre-validated partner data directly into the application flow.
    </p>
    <p>
      With a few clicks, eligible customers could see their quote, complete their application, and
      receive a decision.
    </p>
    <h3>Benefits for customers</h3>
    <ul>
      <li>No repeated data entry</li>
      <li>Faster decisions</li>
      <li>A smoother, more modern buying experience</li>
    </ul>
    <h3>Benefits for partners</h3>
    <ul>
      <li>Lower drop-off</li>
      <li>Higher conversion</li>
      <li>Stronger revenue per customer</li>
    </ul>
    <p>
      This wasn't just a UX improvement. It reshaped how we approached partner launches, compliance
      alignment, funnel strategy, and measurement.
    </p>

    <h2>My role: building the strategy and the operating model</h2>
    <p>
      I led the marketing strategy and execution behind this distribution model, with
      responsibilities spanning:
    </p>
    <ul>
      <li>Partner journey design</li>
      <li>Funnel optimization and testing</li>
      <li>Messaging development and creative strategy</li>
      <li>Multi-channel nurture programs</li>
      <li>Performance analytics and conversion modeling</li>
      <li>
        Cross-functional coordination across product, underwriting, engineering, and compliance
      </li>
      <li>Building playbooks and templates that standardized and sped up launch cycles</li>
    </ul>
    <p>
      The goal: create a scalable, predictable model that partners could adopt quickly, and
      confidently.
    </p>

    <h2>How we achieved the key results</h2>

    <h3>1. 183% conversion lift by removing friction</h3>
    <p>We broke down every step of the funnel and challenged its necessity. This included:</p>
    <ul>
      <li>Pre-filling partner-provided data</li>
      <li>Shortening the application</li>
      <li>Reducing the number of decision screens</li>
      <li>Clarifying insurance language</li>
      <li>Improving mobile usability</li>
      <li>Streamlining compliance-required content without losing meaning</li>
    </ul>
    <p>
      Each improvement removed friction. Combined, they produced a 183% improvement in conversion
      across partner traffic.
    </p>

    <h3>2. 20% of sales recovered through nurture</h3>
    <p>
      Instead of letting declines or abandoned applications go cold, we created a nurture engine:
    </p>
    <ul>
      <li>Follow-up emails and SMS</li>
      <li>Educational content</li>
      <li>Behavioral triggers</li>
      <li>Clear paths back into the funnel</li>
    </ul>
    <p>
      These efforts brought 20% of all sales back into the purchase flow, proving that simplified
      issue customers often need time, not a different product.
    </p>

    <h3>3. 83% faster time to market by standardizing launches</h3>
    <p>Partners often wait months for insurance launches. We collapsed that timeline by creating:</p>
    <ul>
      <li>A partner launch playbook</li>
      <li>Standard creative and compliance templates</li>
      <li>A reuse-first approach for landing pages and messaging</li>
      <li>A partner health dashboard that prioritized the highest-revenue opportunities</li>
      <li>Weekly cross-functional working sessions to eliminate blockages</li>
    </ul>
    <p>
      The result was an 83% improvement in time to market, allowing us to activate more partners
      and test more placements throughout the year.
    </p>

    <h2>Why embedded insurance is the future</h2>
    <p>
      Partner distribution isn't "just a channel." It represents the next generation of insurance
      distribution, one that meets customers where they already are and removes barriers that
      historically slowed adoption.
    </p>
    <p>
      When insurance becomes embedded in banking apps, fintech platforms, loyalty programs, and
      member ecosystems, everyone wins:
    </p>
    <ul>
      <li>Customers get relevant, timely protection</li>
      <li>Partners deepen loyalty and unlock new revenue streams</li>
      <li>Carriers gain efficient, scalable distribution</li>
    </ul>
    <p>
      Few-click experiences accelerate this shift by giving customers an effortless way to move
      from curiosity to coverage.
    </p>

    <h2>Lessons learned</h2>
    <p>Leading this transformation reinforced key truths about modern insurance marketing:</p>
    <ul>
      <li>Simplicity is the competitive advantage</li>
      <li>Speed matters, especially in regulated industries</li>
      <li>Funnel optimization requires cross-functional alignment</li>
      <li>Partner trust grows from reliability and execution</li>
      <li>Nurture strategies dramatically increase lifetime conversion</li>
      <li>Embedded experiences will define the next decade of growth</li>
    </ul>
    <p>
      Insurance is becoming a frictionless component of the digital financial ecosystem, not a
      standalone purchase.
    </p>

    <h2>Looking ahead</h2>
    <p>
      The few-clicks experience and partnership model fundamentally reshaped how Prudential
      connected with millions of potential customers. It also created a blueprint other insurers
      and financial services organizations can adopt as consumer expectations continue to evolve.
    </p>
    <p>
      If you're exploring embedded insurance, partnership distribution, or ways to remove friction
      from regulated customer journeys, I'd be glad to connect. Let's talk about how to accelerate
      growth through smarter distribution and seamless customer experiences.
    </p>
    <p className="mt-8">
      <BookCallButton />
    </p>
  </>
);

const SalesNavigatorFiftyMillion: ComponentType = () => (
  <>
    <p className="lead">
      Today, wealth management teams are expected to do more with less while delivering highly
      personalized service. The challenge: clients engage through many channels, but
      relationship-building still depends on meaningful human connection. Bridging the gap between
      digital engagement and face-to-face conversations is where real growth happens.
    </p>
    <p>
      At U.S. Bank Wealth Management, we built an integrated, multi-channel strategy using LinkedIn
      Sales Navigator, targeted email, SMS, and sales enablement to help relationship managers
      surface real opportunities in their book of business. This combination of strategic coaching
      and smarter digital tools helped drive $50 million in loan growth and AUM, proving that
      digital transformation can create measurable impact in a high-trust, highly regulated
      environment.
    </p>
    <p>
      My experience shows that meaningful financial decisions occur when multiple signals align:
      digital curiosity, timely guidance, and life events that shift priorities. Designing a system
      that syncs these moments together changed how the business approached deepening client
      relationships and identifying money-in-motion opportunities.
    </p>
    <p className="mt-8">
      <BookCallButton variant="subtle" />
    </p>

    <h2>Don't waste my time: the challenge of digital engagement in wealth management</h2>
    <p>
      In wealth management, relationships matter, but attention spans are short. Clients expect
      clarity, relevance, and speed. Relationship managers, on the other hand, need a way to
      identify where to spend their time and how to differentiate a passive digital touch from an
      actual opportunity.
    </p>
    <p>
      The business landscape was shifting quickly. Social media attention was fragmented.
      High-net-worth clients were researching financial options online long before connecting with
      an advisor. And regulatory constraints meant that any digital activity had to be precise,
      compliant, and value-driven.
    </p>
    <p>
      The brief was straightforward: increase meaningful in-person meetings and drive new lending
      opportunities within existing books of business.
    </p>
    <p>
      But the gap was real. Digital marketing was generating engagement, including clicks, opens,
      and content interactions, yet relationship managers lacked a system for interpreting those
      signals or acting on them. That meant missed opportunities during critical life moments such
      as business transitions, real estate decisions, liquidity events, and retirement planning.
    </p>
    <p>
      LinkedIn Sales Navigator became the unlock. Used well, it identifies warm paths, shared
      networks, professional milestones, and triggers that suggest financial decisions are
      approaching. But without training, most teams barely scratch the surface of what Navigator can
      deliver. That's where strategic coaching came in.
    </p>

    <h2>Do it for me: strategically activating sales teams through digital tools</h2>
    <p>
      Supporting wealth management teams through integrated sales enablement makes sense when you
      embrace a "do it for me" approach, one where the marketing function equips relationship
      managers with everything needed to identify, nurture, and convert opportunities. The strategy
      focused on three connected pillars.
    </p>

    <h3>1. Training relationship managers to use Sales Navigator as a prospecting engine</h3>
    <p>Navigator became the core tool for identifying:</p>
    <ul>
      <li>job changes</li>
      <li>company liquidity events</li>
      <li>executive promotions</li>
      <li>industry movement</li>
      <li>geographic ties</li>
      <li>secondary networks leading to warm introductions</li>
    </ul>
    <p>
      By framing Navigator not as a social tool but a financial signal tool, we built confidence
      with teams who were hesitant about digital prospecting.
    </p>

    <h3>2. Using social, email, and SMS to warm prospects before meetings</h3>
    <p>
      Clients rarely jump from a cold call to a high-value meeting. The program created
      multi-channel touchpoints including:
    </p>
    <ul>
      <li>monthly themed newsletters</li>
      <li>curated articles tied to common financial questions</li>
      <li>SMS reminders for upcoming appointments</li>
      <li>targeted follow-ups tied to observed money-in-motion moments</li>
      <li>digital content that reinforced trust and expertise</li>
    </ul>
    <p>
      These touchpoints reduced the lift required of relationship managers and kept them
      consistently top-of-mind in their clients' financial lives.
    </p>

    <h3>3. Aligning digital engagement with money-in-motion triggers</h3>
    <p>Navigator insights, plus life events, plus existing portfolio goals equals actionable opportunity.</p>
    <p>By mapping these elements together, relationship managers could prioritize conversations such as:</p>
    <ul>
      <li>
        "I saw you recently changed roles. How will your compensation structure impact your
        long-term goals?"
      </li>
      <li>
        "Your company is scaling quickly. Is now the right moment to explore liquidity or lending
        options?"
      </li>
    </ul>
    <p>The goal wasn't automation for efficiency. It was automation for clarity.</p>

    <h2>Surround sound: how integrated channels supported in-person meetings</h2>
    <p>
      Just as surround-sound marketing supports large B2B purchases, wealth management requires
      multiple coordinated touchpoints to create urgency and confidence.
    </p>

    <h3>Email</h3>
    <p>Our recurring email series helped clients:</p>
    <ul>
      <li>assess lending needs</li>
      <li>evaluate financial milestones</li>
      <li>understand loan structures</li>
      <li>prepare documentation ahead of meetings</li>
    </ul>
    <p>
      Email also reinforced key Navigator triggers. When a signal surfaced, an aligned email topic
      could warm the conversation.
    </p>

    <h3>SMS</h3>
    <p>SMS was used sparingly but effectively:</p>
    <ul>
      <li>appointment reminders</li>
      <li>follow-ups after digital content engagement</li>
      <li>post-meeting confirmations</li>
    </ul>
    <p>
      Regulated environments require tight controls, and our approach remained compliance-first
      while still adding convenience for clients.
    </p>

    <h3>Social</h3>
    <p>Navigator insights allowed relationship managers to:</p>
    <ul>
      <li>congratulate clients on promotions</li>
      <li>comment on company milestones</li>
      <li>identify warm introductions</li>
      <li>align outreach with professional achievements</li>
    </ul>
    <p>
      These social interactions acted as micro-touchpoints that softened the path to conversations.
    </p>

    <h3>Sales enablement</h3>
    <p>Relationship managers received:</p>
    <ul>
      <li>conversation guides</li>
      <li>prompt frameworks tied to Navigator signals</li>
      <li>compliant message templates</li>
      <li>money-in-motion playbooks</li>
      <li>regular coaching sessions</li>
    </ul>
    <p>The combination gave them confidence to convert digital insights into real business.</p>

    <h2>Execution: how the program worked in practice</h2>

    <h3>1. Teach the tool, then teach the behavior</h3>
    <p>Instead of training Navigator features, we trained Navigator outcomes:</p>
    <ul>
      <li>spotting upcoming mortgage needs</li>
      <li>identifying lending opportunities</li>
      <li>uncovering liquidity signals</li>
      <li>recognizing board appointments or business transitions</li>
    </ul>
    <p>The tool became intuitive when connected to revenue.</p>

    <h3>2. Weekly coaching sprints</h3>
    <p>Each session focused on:</p>
    <ul>
      <li>reviewing Navigator alerts</li>
      <li>setting outreach priorities</li>
      <li>refining messaging</li>
      <li>preparing for upcoming meetings</li>
      <li>tracking outcomes from previous conversations</li>
    </ul>
    <p>This built momentum and shifted behavior from reactive to proactive.</p>

    <h3>3. Integrated campaigns to support outreach</h3>
    <ul>
      <li>"Planning for your next move?" for home purchases or HELOC opportunities</li>
      <li>"Business on the rise?" for lending tied to business expansion</li>
      <li>"Financial milestones checklist" aligned with promotions or career changes</li>
    </ul>
    <p>Every campaign included email, social prompts, and a conversation guide.</p>

    <h3>4. Consistent coordination across marketing and sales</h3>
    <p>
      Digital signals flowed into weekly action lists. Relationship managers acted on them and
      shared outcomes. Marketing refined content based on real conversations. The result was a
      cohesive customer journey from digital engagement to in-person financial planning.
    </p>

    <h2>Results: $50 million in loan growth through a shift in sales behavior</h2>
    <p>
      The headline result is clear: $50 million in loan growth directly connected to the integrated
      strategy. This came from:
    </p>
    <ul>
      <li>increased in-person meetings</li>
      <li>stronger conversations tied to money-in-motion triggers</li>
      <li>higher conversion rates among clients warmed via digital touchpoints</li>
      <li>improved identification of lending opportunities using Navigator insights</li>
    </ul>
    <p>Supporting outcomes included:</p>
    <ul>
      <li>a significant increase in Sales Navigator logins and usage</li>
      <li>stronger pipeline visibility for leaders</li>
      <li>more efficient prioritization of high-value clients</li>
      <li>higher confidence among relationship managers in digital prospecting</li>
      <li>better alignment between marketing activity and sales execution</li>
    </ul>
    <p>
      Before the program, most outreach was reactive, sporadic, and based on instinct. After the
      program, outreach became intentional, insight-driven, supported by digital content, and tied
      to measurable outcomes. Relationship managers expressed higher confidence, better client
      engagement, and more structured conversations around lending needs. The shift was not just
      technological. It was behavioral.
    </p>

    <h2>Lessons learned</h2>
    <p>My experience shows that digital tools don't unlock growth alone. The unlock happens when:</p>
    <ul>
      <li>coaching guides behavior</li>
      <li>insights flow into conversations</li>
      <li>content reinforces value</li>
      <li>channels work together</li>
      <li>compliance is integrated, not an afterthought</li>
    </ul>
    <p>
      When these elements align, the results follow. In this case, $50 million of them. If you want
      to build the same kind of system inside your firm, let's talk.
    </p>
    <p className="mt-8">
      <BookCallButton />
    </p>
  </>
);



const ReferralSystemHiringQuestions: ComponentType = () => (
  <>
    <p className="lead">
      Before hiring someone to build your referral system, ask whether they've actually made
      advisors more referable, not whether they can write a plan. Ask how they design warm outreach
      sequences, how they get content through compliance, and how they tie their work to meetings and
      revenue. Get specifics, not case study slides.
    </p>
    <p>
      A referral system is the bridge between your reputation and your pipeline. You can be the
      most trusted advisor in your market and still have a thin pipeline if no systematic process
      turns relationships into introductions. Hiring the wrong person here doesn't just waste budget.
      It burns credibility with the advisors and referral partners you need on your side.
    </p>
    <p>
      You don't have to be a marketing expert to hire well. You need to ask the right questions
      before you sign anything. Below are the eight that matter most, and what a good answer sounds
      like.
    </p>
    <p className="mt-8">
      <BookCallButton variant="subtle" />
    </p>

    <h2>1. Have you built referral systems advisors actually use, or just ones that launched?</h2>
    <p>
      Ask for adoption numbers, not deliverables. A consultant who has done this work will talk
      about meeting volume, referral introductions, content usage rates, and advisor feedback, not
      just "we shipped an email." If they can't tell you how advisors won more accounts with their
      work, they weren't close enough to the outcome.
    </p>

    <h2>2. How do you design warm outreach sequences that don't feel like cold prospecting?</h2>
    <p>
      A referral system should feel personal, not automated. Ask how they segment your network, what
      triggers a touch, and how they write outreach that sounds like you, not a generic template. A
      good answer names specific principles: relevance first, value before ask, and a clear next
      step that respects the relationship.
    </p>

    <h2>3. What's your experience getting referral content and scripts through compliance?</h2>
    <p>
      This is the step that kills most advisory marketing projects. Ask how they've worked with
      compliance and legal stakeholders, not just marketing. A consultant with real experience here
      can describe a specific process for pre-clearing scripts, email language, and social posts,
      not just "we'll figure it out together."
    </p>

    <h2>4. How do you measure success, and does it include revenue?</h2>
    <p>
      Activity is a start. Revenue influenced is the real number. Ask how they'd track whether your
      referral system turned into meetings, pipeline, and closed business, not just how many emails
      went out or how many advisors logged in.
    </p>

    <h2>5. Which platforms and channels have you implemented, not just used?</h2>
    <p>
      LinkedIn, email, SMS, CRM, Seismic, Hearsay, Salesforce — whatever your stack runs on. Ask
      if they've led a rollout, not just administered a platform someone else selected. Running a
      tool and designing the system around it are different jobs.
    </p>

    <h2>6. How do you get alignment across sales, compliance, and firm leadership?</h2>
    <p>
      A referral system touches more stakeholders than almost any other marketing function. Ask how
      they run alignment: cadence, escalation path, who signs off on what. A vague answer here
      predicts a stalled project later.
    </p>

    <h2>7. What's the timeline, and what typically slows this down?</h2>
    <p>
      Ask for a realistic estimate and where rollouts usually get stuck: compliance review, content
      production, advisor onboarding, or stakeholder sign-off. Someone who's run this before will
      tell you upfront, not after you've missed a launch date.
    </p>

    <h2>8. Can I talk to a firm you've done this for?</h2>
    <p>
      Ask for a reference who can speak to what it's like day to day, not just the final results.
      You want to hear how the consultant handled the messy middle: the first advisor who didn't
      adopt, the compliance revision, the moment the system started producing meetings.
    </p>

    <h2>The bottom line</h2>
    <p>
      Hiring someone to build your referral system doesn't have to be a guessing game. Ask about
      advisor adoption, warm outreach design, compliance, platform experience, and revenue impact
      before you sign anything, and you'll walk in with clarity instead of crossed fingers.
    </p>

    <h2>Where I come in</h2>
    <p>
      I've built referral engines and inbound pipeline systems at this scale before: 900 advisor
      meetings driven monthly, $50M in influenced revenue, multi-channel sequences across email,
      SMS, LinkedIn, and sales enablement, and stakeholder alignment across 30+ VPs including
      compliance and legal. If you're evaluating who to bring in, these are the questions I'd want you
      to ask me too.
    </p>
    <p>
      Let's talk about what your referral system should look like. Reach me at{" "}
      <a href="mailto:katie@katiefernands.com">katie@katiefernands.com</a>.
    </p>
    <p className="mt-8">
      <BookCallButton />
    </p>
  </>
);

const LinkedInLeadGeneration: ComponentType = () => (
  <>
    <p className="lead">
      Most advisors treat LinkedIn like a bulletin board. They post occasionally, connect with
      people they already know, and wonder why nothing comes of it. LinkedIn works for advisory
      firms, but only when it stops being a broadcast channel and starts being a system for turning
      visibility into conversations you don't have to chase.
    </p>
    <p>
      This is the approach I've used inside U.S. Bank Wealth Management and Prudential Financial,
      and now with RIAs, banks, and fintech teams of four to twenty people. No cold calls. No
      spray-and-pray connection requests. Just a repeatable way to be visible to the people who
      already trust you, and to the people they can introduce you to.
    </p>
    <p className="mt-8">
      <BookCallButton variant="subtle" />
    </p>

    <h2>Why LinkedIn works differently for financial advisors</h2>
    <p>
      People don't choose a financial professional the way they choose a vendor. They choose based
      on familiarity, credibility, and someone they already know saying "talk to her." LinkedIn is
      the only channel where all three of those things can happen in public, at scale, without you
      pitching anyone.
    </p>
    <p>
      That changes what you're optimizing for. You're not trying to generate clicks. You're trying
      to be the name that surfaces when a centre of influence, a former colleague, or a prospect's
      accountant is asked "do you know anyone good?"
    </p>

    <h2>Step 1: Fix the profile before you post anything</h2>
    <p>
      Your profile is a landing page, not a résumé. When someone gets a referral to you, the first
      thing they do is look you up. If your headline says "Financial Advisor at Firm," you've
      wasted the visit.
    </p>
    <ul>
      <li>
        <strong>Headline:</strong> who you help and what changes. "I help business owners in
        transition protect what they've built" beats a job title every time.
      </li>
      <li>
        <strong>About section:</strong> written to the client, not about you. Name the problem
        first, your credentials second.
      </li>
      <li>
        <strong>Featured section:</strong> one clear next step. A calendar link, a guide, or a short
        explainer. Not five competing options.
      </li>
      <li>
        <strong>Banner:</strong> plain language on what you do and who for. It's free real estate
        most advisors leave blank.
      </li>
    </ul>

    <h2>Step 2: Build the audience you already have</h2>
    <p>
      Before you chase strangers, mine what exists. Former colleagues, clients you've lost touch
      with, accountants and attorneys you've worked alongside, alumni networks, and the people who
      attended the same conferences you did. These are warm. They just aren't organized.
    </p>
    <p>
      Export your connections, tag them by relationship type, and identify the twenty to fifty
      people who could realistically introduce you to your ideal client. That's your visibility
      audience. Everything you publish should make sense to them.
    </p>

    <h2>Step 3: Post so the right people remember you</h2>
    <p>
      Consistency matters more than volume. Two posts a week for six months beats daily posting for
      three weeks and then silence. A rotation that works for advisory firms:
    </p>
    <ul>
      <li>
        <strong>Observation posts:</strong> something you noticed in a client conversation this
        week, with the identifying details stripped out.
      </li>
      <li>
        <strong>Explainer posts:</strong> one financial concept made plain. Not "5 retirement
        tips." One idea, one post.
      </li>
      <li>
        <strong>Point-of-view posts:</strong> where you disagree with conventional advice, and why.
        These are the ones that get remembered and forwarded.
      </li>
      <li>
        <strong>Proof posts:</strong> outcomes, anonymized and compliance-cleared. What changed for
        someone after they got organized.
      </li>
    </ul>
    <p>
      Run everything through your compliance process before it goes out. Build a small library of
      pre-approved posts so review never becomes the reason you stopped publishing.
    </p>

    <h2>Step 4: Warm outreach, not cold outreach</h2>
    <p>
      Cold connection requests with a pitch attached are why most advisors think LinkedIn doesn't
      work. The sequence that does work is slower and far more effective:
    </p>
    <ol>
      <li>Engage with their content genuinely for a couple of weeks. No agenda.</li>
      <li>Connect with a note that references something specific, not a template.</li>
      <li>
        Send something useful with no ask attached. An article, an introduction, a relevant
        observation.
      </li>
      <li>
        Only then suggest a conversation, and frame it as a conversation, not a pitch. "Worth 20
        minutes to compare notes?" converts better than any pitch you can write.
      </li>
    </ol>
    <p>
      One of the advisors I work with described the result this way: the program helped him land a
      meeting with a former contact purely because he'd become visible and consistent again. That's
      the whole mechanism.
    </p>

    <h2>Step 5: Use Sales Navigator for timing, not volume</h2>
    <p>
      Sales Navigator's value isn't more names. It's knowing when something changed. Job changes,
      promotions, funding events, company moves, and liquidity signals tell you when someone's
      financial life is in motion, which is when they're actually open to a conversation.
    </p>
    <p>
      Set saved searches on your target accounts and relationship map, then let the alerts drive
      your outreach calendar instead of a quota. At U.S. Bank, that pairing of trigger alerts with
      coached, consistent outreach contributed to $50 million in loan growth.
    </p>

    <h2>Step 6: Measure the things that predict revenue</h2>
    <p>
      Impressions are the vanity metric. Track profile views from your target audience, connection
      acceptance rate, inbound messages, introductions requested, and meetings scheduled. Meetings
      scheduled is the number that matters. Everything else is a leading indicator of it.
    </p>

    <h2>What 30 days looks like</h2>
    <p>
      Week one: profile rebuilt and audience tagged. Week two: content library approved and posting
      started. Week three: warm outreach sequences running against your top fifty relationships.
      Week four: first conversations booked, and a measurement view you can actually read.
    </p>
    <p>
      This isn't complicated work. It's just work most firms never sequence properly, which is why
      it stalls at "we should post more."
    </p>
    <p>
      If you'd like help building this for your firm, reach me at{" "}
      <a href="mailto:katie@katiefernands.com">katie@katiefernands.com</a>.
    </p>
    <p className="mt-8">
      <BookCallButton />
    </p>
  </>
);


const InsideAReferralSystem: ComponentType = () => (
  <>
    <p className="lead">
      Your team spends about 20% of their week actually talking to clients. That's not a guess,
      it's the finding from Kitces Research on how advisors really spend their time. The other 80%
      goes to prep, admin, and busywork that feels productive but doesn't move a single
      relationship forward.
    </p>
    <p>
      Here's the harder question underneath that stat: when your team isn't prospecting, is that
      because they don't have time, or because they don't have a system telling them what to do
      with the time they have?
    </p>
    <p>Most firm leaders assume it's the first one. It's almost always the second.</p>

    <h2>Why "we have a referral process" usually isn't true</h2>
    <p>
      Ask most 10-person RIAs how referrals come in, and you'll get some version of "our senior
      advisors are good at it." That's not a system. That's two or three people's personal habits,
      wearing a system's clothes.
    </p>
    <p>
      It works, right up until it doesn't. An advisor leaves. A rainmaker gets pulled into other
      priorities. A newer advisor joins and has no idea what "good at referrals" actually means in
      practice, because nobody ever wrote it down. The firm finds out in real time that what it
      called a referral engine was actually one person's relationships.
    </p>
    <p>
      This isn't a sales problem. It's a documentation problem. And it's fixable in a way that
      "hire better closers" never is.
    </p>

    <h2>The four parts of a system that survives any one person's calendar</h2>
    <p>
      A referral system that actually holds up has four parts. None of them are complicated on
      their own. The failure mode is almost always that firms do one or two of these and stop.
    </p>
    <h3>1. Document what's already working</h3>
    <p>
      Before you build anything new, map where your current referrals actually come from. Not
      where you assume they come from, where they actually come from. Most firms are surprised.
      The client who's referred four people in two years rarely looks like the client leadership
      expected to be the firm's best advocate.
    </p>
    <h3>2. Delegate what shouldn't require your rainmakers</h3>
    <p>
      Your best advisors are good at relationships, not necessarily at teaching relationships.
      Mentorship of newer or less seasoned advisors needs to be structured, repeatable, and
      focused, not left to whoever has bandwidth that week. When it isn't, newer staff get handed
      a task list instead of a playbook, and they default to what they know, admin work, because
      nobody showed them what prospecting actually looks like day to day.
    </p>
    <h3>3. Deploy a visibility plan, not a one-off ask</h3>
    <p>
      Referrals rarely come from a single well-timed question. They come from consistent,
      low-friction visibility, showing up in a way that keeps you top of mind before you ever need
      to ask for anything. A visibility plan is specific: what gets shared, how often, and through
      which channel, mapped to your actual client base.
    </p>
    <h3>4. Track it, and use the data to reactivate what's gone quiet</h3>
    <p>
      Most firms track new business. Almost none track referral sources specifically, which means
      they can't tell you what's actually working. That gap also means dormant relationships stay
      dormant, because nobody's watching for them.
    </p>

    <h2>What this looks like in practice</h2>
    <p>
      At U.S. Bank Wealth Management, we built referral systems across HNW and UHNW client
      segments using exactly this structure. One relationship stood out. A client relationship had
      gone quiet for over a year, no complaints, no drama, just silence. Under a documented,
      consistent visibility plan, not a cold outreach script, that relationship came back. The
      system didn't rely on someone remembering to check in. It made checking in automatic.
    </p>
    <p>
      That's the difference between hoping referrals show up and building something that makes
      sure they do.
    </p>

    <h2>The first 90 days</h2>
    <p>You don't need to build all four parts at once. A realistic sequence:</p>
    <p>
      <strong>Days 1-30:</strong> Document your actual referral sources for the last 12-24 months.
      Interview your top two or three referral generators about what they actually do, not what
      they think they do.
    </p>
    <p>
      <strong>Days 31-60:</strong> Build the mentorship structure. Turn what you learned in step
      one into a playbook a newer advisor could follow without a rainmaker walking them through it
      live.
    </p>
    <p>
      <strong>Days 61-90:</strong> Stand up the visibility plan and start tracking referral
      sources going forward, not just closed business. This is also when it's worth reviewing your
      dormant-relationship list, the accounts that have gone quiet, for reactivation.
    </p>

    <h2>Frequently asked questions</h2>
    <p>
      <strong>How does this work, and what if my team doesn't know how to use social media?</strong>
      <br />
      They don't need to become content creators. A visibility plan turns what your team already
      does well, talking to clients, into something repeatable the whole firm can run. Social
      visibility is one input, not the whole system.
    </p>
    <p>
      <strong>
        How is this different from just telling advisors to ask for referrals more often?
      </strong>
      <br />
      Asking more often without a system just produces more inconsistent asks. Documentation and
      tracking are what make referrals repeatable instead of dependent on any one person
      remembering to follow up.
    </p>
    <p>
      <strong>What if our rainmakers don't have time to help build this?</strong>
      <br />
      That's the point. The system is designed so their time goes into the relationships only they
      can hold, not into teaching by osmosis. Step one starts with an interview, not a time
      commitment.
    </p>
    <p>
      <strong>How long before we see results?</strong>
      <br />
      Documentation and tracking show gaps almost immediately. Referral volume changes take
      longer, typically two to three quarters, since it depends on your current pipeline and how
      dormant your existing client base is.
    </p>
    <p>
      <strong>Do we need new technology to do this?</strong>
      <br />
      No. This is a process and documentation problem first. Most firms already have the tools;
      they don't have the system telling them how to use them.
    </p>

    <p className="mt-8">
      <BookCallButton label="Book a 30-minute discovery call" />
    </p>
  </>
  );

const WhySatisfiedClientsDontRefer: ComponentType = () => (
  <>
    <p className="lead">
      A client can like you, trust you, and renew with you every single year, and still never send
      you a referral. Not because they're unwilling. Because satisfaction and advocacy are two
      different things, and most firms only ever build the first one.
    </p>
    <p>
      Satisfaction means "I'm happy here." Advocacy means "I know exactly who should talk to you,
      why it matters, and how to make the introduction." Those are not the same behavior, and a
      firm that's optimized for the first one can be genuinely confused about why it isn't getting
      more of the second.
    </p>

    <h2>The two gaps that keep happy clients quiet</h2>
    <p>
      There are really 2 separate problems hiding inside "our clients love us but don't refer." Most
      firms only ever notice one of them.
    </p>
    <p>
      <strong>The language gap.</strong> Your client may not know who you help best, when your firm
      is actually relevant to someone they know, or how to introduce you without it feeling awkward.
      So they say nothing, not out of reluctance, but because the mental work of explaining you is
      harder than the goodwill they feel toward you.
    </p>
    <p>
      <strong>The moment gap.</strong> Even when a client could explain what you do, advocacy has to
      be earned at specific points in the relationship, not manufactured at the annual review. If
      your firm never shows up clearly at those moments, the goodwill never gets a chance to turn
      into a referral in the first place.
    </p>
    <p>
      Fix only one of these and you'll still be stuck. Give clients perfect language with no real
      moment of relevance, and you've handed them a script for a conversation they'll never have.
      Create the perfect moment with no language to describe you, and the client feels the impulse to
      refer but can't act on it.
    </p>

    <h2>Closing the language gap</h2>
    <p>The fix here isn't a better ask. It's specificity.</p>
    <p>
      <strong>Define the referral fit in plain English.</strong> Not "comprehensive wealth
      management for high-net-worth families." Try something like: "We're especially helpful for
      business owners 5 to 10 years from an exit who need to coordinate tax, estate, and investment
      decisions." Specific language gives a client someone real to picture, a friend, a colleague,
      a name, instead of a vague category they have to translate on your behalf.
    </p>
    <p>
      <strong>Give clients approved language they can actually use.</strong> Most clients don't
      want to write your pitch for you. Handing them a clean, human sentence, something like "I work
      with a team that helps business owners think through planning before a sale, if it would be
      useful I'm happy to introduce you", removes the blank page. It's specific enough to sound real
      and generic enough that it doesn't feel like a script.
    </p>
    <p>
      <strong>Build the ask into moments that already make sense</strong>, rather than saving it for
      a single annual "do you know anyone?" A complex planning win, a business transition
      conversation, a client mentioning a friend in a similar situation, these are all better openings
      than a once-a-year prompt, because the relevance is already high and the timing feels useful
      instead of forced.
    </p>

    <h2>Closing the moment gap</h2>
    <p>
      Language solves what a client would say. It doesn't solve when they'd think to say it. That's a
      separate problem, and it lives earlier in the relationship than most firms look.
    </p>
    <p>
      Clients become advocates because, over time, your firm makes them feel 3 things: understood,
      supported, and confident enough to introduce you. That feeling gets built or lost at specific,
      recurring moments, well before anyone brings up referrals directly.
    </p>
    <p>
      <strong>Onboarding is a referral moment.</strong> Does the client feel like your team already
      understands their complexity, or are they repeating the same details for the third time?
    </p>
    <p>
      <strong>Review meetings are a referral moment.</strong> Do you connect the plan back to
      what actually matters in the client's life, or walk through performance numbers while they nod
      politely?
    </p>
    <p>
      <strong>Life transitions are a referral moment.</strong> A business sale, an inheritance, a
      divorce, a parent who needs care, these carry real emotional weight. If your team shows up
      clearly during them, clients remember it long after the transition is over.
    </p>
    <p>
      <strong>Proactive follow-up is a referral moment.</strong> The note sent after a meeting
      without being asked. The next step handled before the client has to request it. The call
      placed when markets are noisy, when silence would have technically been fine but felt like
      absence.
    </p>
    <p>
      <strong>Clear positioning is a referral moment</strong>, in the sense that it either enables
      or blocks every other one. If a client can't explain who you help best, they can't recognize
      the right person to introduce, even when the moment is right in front of them.
    </p>
    <p>
      None of these moments require a bigger budget or a new hire. They require your team to notice
      that advocacy is being built or lost in ordinary client interactions, not manufactured at the
      end of a meeting with a well-timed question.
    </p>

    <h2>What this looks like together</h2>
    <p>
      A client goes quiet for a year. No complaints, no drama, just silence. Nothing about their
      satisfaction has changed, they still like the firm, they just haven't been given a reason or a
      moment to act on it.
    </p>
    <p>
      A documented visibility plan, consistent and low-key rather than a single outreach push, closes
      both gaps at once. It gives the firm language to use consistently, and it creates recurring,
      low-friction moments of relevance instead of relying on the client to remember the firm
      unprompted. That's the difference between hoping a relationship reactivates and building
      something that makes sure it does.
    </p>
    <p>
      I walk through the four-part system behind that visibility plan in{" "}
      <Link
        to="/blog/$slug"
        params={{ slug: "inside-a-referral-system" }}
        className="text-primary hover:underline"
      >
        Inside a Referral System: What It Actually Looks Like at a 10-Person RIA
      </Link>
      .
    </p>

    <h2>Where to start this week</h2>
    <p>You don't need to overhaul the client experience to close these gaps. Start narrower.</p>
    <p>
      <strong>Days 1-14:</strong> Write the plain-English referral fit description above, and one
      piece of approved language your team can use verbatim. Test it in a handful of real
      conversations before rolling it out further.
    </p>
    <p>
      <strong>Days 15-30:</strong> Map your own client journey against the five referral moments.
      Pick the one your firm is weakest at, onboarding, reviews, transitions, follow-up, or
      positioning, and fix that one first rather than all five at once.
    </p>
    <p>
      <strong>Days 31-60:</strong> Build a short list of clients who've gone quiet despite no
      complaint on file. Apply the language and the moment-based approach to reopen a handful of
      those relationships, and watch what happens.
    </p>

    <h2>Frequently asked questions</h2>
    <p>
      <strong>Isn't asking directly just faster than all of this?</strong>
      <br />
      It's faster for one ask. It doesn't scale, and it puts the entire burden of a referral on the
      client's memory in a single moment. Language and moments build a pattern that keeps working
      without you having to ask at all.
    </p>
    <p>
      <strong>
        What if our clients are genuinely private people who wouldn't refer regardless?
      </strong>
      <br />
      Some won't, and that's fine. This isn't about pressuring reluctant clients. It's about the
      much larger group who would refer, but currently don't have the words or the moment to act on
      real goodwill they already feel.
    </p>
    <p>
      <strong>Does this require our advisors to change how they talk to clients?</strong>
      <br />
      Only slightly, and mostly in the direction of removing improvisation, not adding a script.
      Advisors already have these conversations. The fix is making sure a few of them include
      specific, repeatable language instead of leaving it to whatever comes to mind that day.
    </p>
    <p>
      <strong>
        How is this different from just training advisors to ask for referrals more often?
      </strong>
      <br />
      Asking more often without fixing the language and moment gaps just produces more inconsistent,
      awkward asks. This approach fixes what happens before the ask, so that when it does come up, it
      lands on a client who already has the words and the reason.
    </p>
    <p>
      <strong>How long before this shows up in actual referral volume?</strong>
      <br />
      The language piece can show up in conversations almost immediately. The moment-based piece
      takes longer, typically 2 to 3 quarters, since it depends on naturally occurring life events
      and review cycles across your client base.
    </p>

    <p>
      If your team is sitting on satisfied clients who could become your best referral source, but
      nobody's ever heard why, that's not a satisfaction problem. See how{" "}
      <Link to="/" className="text-primary hover:underline">
        Katie Fernands
      </Link>{" "}
      helps advisory firms build referral systems, or book a 30-minute discovery call to talk
      through what closing that gap would look like for your firm.
    </p>
    <p className="mt-8">
      <BookCallButton label="Book a 30-minute discovery call" />
    </p>
  </>
);

export const articleRedirects: Record<string, string> = {
  "questions-to-ask-before-hiring-an-advisor-enablement-consultant":
    "questions-to-ask-before-hiring-someone-to-build-your-referral-system",
};

export const articles: Record<string, Article> = {
  "linkedin-lead-generation-for-financial-advisors": {
    slug: "linkedin-lead-generation-for-financial-advisors",
    title: "LinkedIn Lead Generation for Financial Advisors",
    titleTag: "LinkedIn Lead Generation for Financial Advisors",
    metaDescription:
      "A step-by-step LinkedIn lead generation system for RIAs, banks, and fintech advisory teams. Turn visibility into inbound conversations and referrals, without cold calls.",
    date: "2026-07-28",
    readingTime: "8 min read",
    keywords: [
      "LinkedIn lead generation for financial advisors",
      "LinkedIn for RIAs",
      "financial advisor LinkedIn strategy",
      "wealth management LinkedIn marketing",
      "LinkedIn Sales Navigator for advisors",
      "advisor referral system",
    ],
    faq: [
      {
        question: "Does LinkedIn actually generate leads for financial advisors?",
        answer:
          "Yes, when it is run as a system rather than occasional posting. The leads come from consistent visibility with people who already know you, plus warm outreach timed around real life events, not from cold connection requests.",
      },
      {
        question: "How often should a financial advisor post on LinkedIn?",
        answer:
          "Two posts a week sustained for six months outperforms daily posting for three weeks. Consistency is what builds recall with centres of influence and prospects.",
      },
      {
        question: "Is LinkedIn Sales Navigator worth it for an RIA?",
        answer:
          "It is worth it if you use it for timing rather than volume. Alerts on job changes, promotions, and company moves tell you when someone's financial life is in motion, which is when outreach lands.",
      },
      {
        question: "How do advisors stay compliant when posting on LinkedIn?",
        answer:
          "Build a library of pre-approved posts, run everything through your existing review process, and follow the SEC marketing rule disclosure requirements when referencing clients or outcomes. Confirm specifics with your CCO.",
      },
      {
        question: "How long before LinkedIn produces meetings?",
        answer:
          "With a rebuilt profile, an organized audience, and warm outreach sequences running, most firms see the first conversations inside 30 days and compounding inbound after several months.",
      },
    ],
    Body: LinkedInLeadGeneration,
  },

  "questions-to-ask-before-hiring-someone-to-build-your-referral-system": {
    slug: "questions-to-ask-before-hiring-someone-to-build-your-referral-system",
    title: "What Questions Should I Ask Before Hiring Someone to Build My Referral System?",
    titleTag: "8 Questions to Ask Before Hiring a Referral System Consultant",
    metaDescription:
      "The eight questions to ask before hiring someone to build a referral system for your advisory firm, covering strategy, compliance, advisor adoption, and revenue impact.",
    date: "2026-07-28",
    readingTime: "6 min read",
    keywords: [
      "referral marketing for financial advisors",
      "referral system for financial advisors",
      "financial advisor marketing plan",
      "hiring a marketing consultant",
      "how to build a referral system",
      "financial advisor referral engine",
    ],
    image: {
      url: advisorEnablementLake.url,
      alt: "A single canoe on a calm mountain lake, framed by evergreens and cliffs, representing the clarity and guidance needed to build a referral system",
    },
    faq: [
      {
        question: "What does a referral system consultant do for a financial advisory firm?",
        answer:
          "They design the strategy, content, sequences, and processes that turn your existing relationships into a repeatable source of introductions and meetings, then measure whether advisors adopt the system and whether it produces revenue.",
      },
      {
        question: "How do you measure the success of a referral system?",
        answer:
          "Start with adoption: advisor usage, referral introductions, and meeting volume. Then tie it to revenue influenced, meaning deals where the referral system touched the conversation.",
      },
      {
        question: "Why do referral system projects stall?",
        answer:
          "Compliance review, content production, advisor onboarding, and stakeholder sign-off are the four usual culprits. A consultant who has run this before will name them upfront and plan around them.",
      },
      {
        question: "Should a referral system consultant have platform experience?",
        answer:
          "Yes. Administering a tool someone else selected is different from leading a rollout across LinkedIn, email, CRM, or sales enablement platforms. Ask which channels and platforms they have actually implemented.",
      },
    ],
    Body: ReferralSystemHiringQuestions,
  },


  "linkedin-sales-navigator-50-million-us-bank": {
    slug: "linkedin-sales-navigator-50-million-us-bank",
    title: "How LinkedIn Sales Navigator Drove $50 Million at U.S. Bank",
    titleTag: "LinkedIn Sales Navigator: $50M in Growth at U.S. Bank",
    metaDescription:
      "How coaching, LinkedIn Sales Navigator, email, and SMS turned digital signals into in-person meetings and $50 million in loan growth at U.S. Bank Wealth Management.",
    date: "2026-05-20",
    readingTime: "8 min read",
    keywords: [
      "LinkedIn Sales Navigator",
      "wealth management prospecting",
      "sales enablement for advisors",
      "money in motion triggers",
      "financial advisor marketing",
      "digital prospecting in banking",
    ],
    image: {
      url: salesNavigatorPhoto.url,
      alt: "Two professionals in a strategy session at a bright table with notebooks, a tablet, and a laptop",
    },
    Body: SalesNavigatorFiftyMillion,
  },

  "selling-insurance-in-5-minutes-or-less": {
    slug: "selling-insurance-in-5-minutes-or-less",
    basePath: "/f",
    title: "A/B Tests Drove a 183% Lift in Conversions",
    titleTag: "Selling Insurance in 5 Minutes: A 183% Conversion Lift",
    metaDescription:
      "How a few-clicks, embedded distribution model at Prudential drove a 183% conversion lift, recovered 20% of sales through nurture, and cut launch time by 83%.",
    date: "2026-02-18",
    readingTime: "7 min read",
    keywords: [
      "embedded insurance",
      "partnership distribution",
      "simplified issue life insurance",
      "insurance conversion rate optimization",
      "few-clicks insurance",
      "insurance funnel optimization",
    ],
    image: {
      url: familyPhoto.url,
      alt: "A family of four embracing in a bright room, the kind of household simplified issue life insurance is designed to protect",
    },
    Body: SellingInsuranceInFiveMinutes,
  },

  "financial-advisor-marketing-plan": {
    slug: "financial-advisor-marketing-plan",
    title: "The Financial Advisor Marketing Plan That Actually Fills Your Pipeline",
    titleTag: "The Financial Advisor Marketing Plan That Works in 2026",
    metaDescription:
      "A step-by-step marketing plan for RIAs, banks, and fintech advisors, built on inbound, LinkedIn, and warm outreach. No cold calls.",
    date: "2026-07-16",
    readingTime: "9 min read",
    keywords: [
      "financial advisor marketing plan",
      "marketing plan for financial advisors",
      "RIA marketing strategy",
      "how to market a financial advisory firm",
      "financial advisor lead generation",
      "inbound marketing for financial advisors",
    ],
    faq: [
      {
        question: "How much should a financial advisor spend on marketing?",
        answer:
          "Kitces Research puts the average around 7% of revenue, though the number matters less than where it goes. Most of your real cost is time, not ad spend, so track both.",
      },
      {
        question: "What's the best marketing channel for financial advisors?",
        answer:
          "There's no single best channel. Referrals convert best per dollar spent, but they don't scale on their own. LinkedIn and long-form content build the system that makes referrals and warm outreach work better over time.",
      },
      {
        question: "Can financial advisors use client testimonials?",
        answer:
          "Yes, since the SEC's 2021 marketing rule. You have to disclose whether the person is a client and whether they were compensated, and in most cases you'll need a written agreement with them. Confirm the specifics with your CCO.",
      },
      {
        question: "How long does inbound marketing take to work for advisors?",
        answer:
          "Plan on six to twelve months before it compounds into a predictable flow of conversations. It's slower to start than cold outreach and faster to scale once it's running.",
      },
      {
        question: "Do I need a marketing agency, or can I do this myself?",
        answer:
          "You can build the framework yourself. What's harder to do alone is staying consistent for the first six months before you see traction, and knowing which of the five parts is actually your weak link. That's usually where outside help pays for itself.",
      },
    ],
    Body: FinancialAdvisorMarketingPlan,
  },

  "inside-a-referral-system": {
    slug: "inside-a-referral-system",
    title: "Inside a Referral System: What It Actually Looks Like at a 10-Person RIA",
    titleTag: "Inside a Referral System: What It Actually Looks Like at a 10-Person RIA",
    metaDescription:
      "A referral system isn't a CRM or a mood. Here's the 4-part framework RIA firms use to make referrals repeatable instead of personality-dependent.",
    date: "2026-09-10",
    readingTime: "7 min read",
    keywords: [
      "referral system for RIA firms",
      "RIA referral system",
      "how to build a referral engine",
      "referral process for financial advisors",
      "RIA growth strategy",
      "advisor visibility plan",
    ],
    image: {
      url: "https://images.unsplash.com/photo-1445400729573-1f666abb9447?w=1200&h=630&fit=crop&q=80",
      alt: "Time-lapse of a busy train station, representing a repeatable, structured system",
    },
    faq: [
      {
        question: "How does this work, and what if my team doesn't know how to use social media?",
        answer:
          "They don't need to become content creators. A visibility plan turns what your team already does well, talking to clients, into something repeatable the whole firm can run. Social visibility is one input, not the whole system.",
      },
      {
        question:
          "How is this different from just telling advisors to ask for referrals more often?",
        answer:
          "Asking more often without a system just produces more inconsistent asks. Documentation and tracking are what make referrals repeatable instead of dependent on any one person remembering to follow up.",
      },
      {
        question: "What if our rainmakers don't have time to help build this?",
        answer:
          "That's the point. The system is designed so their time goes into the relationships only they can hold, not into teaching by osmosis. Step one starts with an interview, not a time commitment.",
      },
      {
        question: "How long before we see results?",
        answer:
          "Documentation and tracking show gaps almost immediately. Referral volume changes take longer, typically two to three quarters, since it depends on your current pipeline and how dormant your existing client base is.",
      },
      {
        question: "Do we need new technology to do this?",
        answer:
          "No. This is a process and documentation problem first. Most firms already have the tools; they don't have the system telling them how to use them.",
      },
    ],
    Body: InsideAReferralSystem,
  },

  "why-satisfied-clients-dont-refer": {
    slug: "why-satisfied-clients-dont-refer",
    title: "Why Satisfied Clients Don't Refer You (And What Actually Earns Advocacy)",
    titleTag: "Why Satisfied Clients Don't Refer You (And What Actually Earns Advocacy)",
    metaDescription:
      "Client satisfaction and client advocacy aren't the same thing. Here's the gap that keeps happy clients quiet, and the 2-part framework that closes it.",
    date: "2026-09-11",
    readingTime: "8 min read",
    keywords: [
      "why satisfied clients don't refer financial advisors",
      "client advocacy vs client satisfaction",
      "how to get more referrals from happy clients",
      "referral moments in the client journey",
      "referral system for RIA firms",
      "financial advisor referral strategy",
    ],
    image: {
      url: "https://images.unsplash.com/photo-1759105764651-5e1e7124cc7a?w=1200&h=630&fit=crop&q=80",
      alt: "A book, candle, and apples on a table, a quiet still life",
    },
    faq: [
      {
        question: "Isn't asking directly just faster than all of this?",
        answer:
          "It's faster for one ask. It doesn't scale, and it puts the entire burden of a referral on the client's memory in a single moment. Language and moments build a pattern that keeps working without you having to ask at all.",
      },
      {
        question: "What if our clients are genuinely private people who wouldn't refer regardless?",
        answer:
          "Some won't, and that's fine. This isn't about pressuring reluctant clients. It's about the much larger group who would refer, but currently don't have the words or the moment to act on real goodwill they already feel.",
      },
      {
        question: "Does this require our advisors to change how they talk to clients?",
        answer:
          "Only slightly, and mostly in the direction of removing improvisation, not adding a script. Advisors already have these conversations. The fix is making sure a few of them include specific, repeatable language instead of leaving it to whatever comes to mind that day.",
      },
      {
        question: "How is this different from just training advisors to ask for referrals more often?",
        answer:
          "Asking more often without fixing the language and moment gaps just produces more inconsistent, awkward asks. This approach fixes what happens before the ask, so that when it does come up, it lands on a client who already has the words and the reason.",
      },
      {
        question: "How long before this shows up in actual referral volume?",
        answer:
          "The language piece can show up in conversations almost immediately. The moment-based piece takes longer, typically 2 to 3 quarters, since it depends on naturally occurring life events and review cycles across your client base.",
      },
    ],
    Body: WhySatisfiedClientsDontRefer,
  },
};
